import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { Navbar } from '../components/Navbar';
import { CaseStudyHero } from '../components/CaseStudyHero';
import { CaseStudySection } from '../components/CaseStudySection';
import { ContentNavigationChaos } from '../components/ContentNavigationChaos';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Users, Layout, Zap, Clock, Star, MessageSquare, Grid, Shield, Activity, MoveHorizontal, ChevronDown, User, Check, X, Info } from 'lucide-react';
// --- Internal Components ---
function BeforeAfterSlider({
  beforeImage,
  afterImage
}: {
  beforeImage: string;
  afterImage: string;
}) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const handleMove = (clientX: number) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percentage = x / rect.width * 100;
      setSliderPosition(percentage);
    }
  };
  const handleMouseDown = () => {
    isDragging.current = true;
  };
  const handleMouseUp = () => {
    isDragging.current = false;
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current) {
      handleMove(e.clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isDragging.current = false;
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);
  return <div ref={containerRef} className="relative w-full aspect-video rounded-xl overflow-hidden cursor-ew-resize select-none border border-white/10 shadow-2xl" onMouseMove={handleMouseMove} onMouseDown={handleMouseDown} onTouchMove={handleTouchMove} data-id="element-324">
      {/* After Image (Background) */}
      <img src={afterImage} alt="After" className="absolute inset-0 w-full h-full object-cover" data-id="element-325" />
      <div className="absolute top-4 right-4 bg-black/50 backdrop-blur px-3 py-1 rounded text-xs font-bold text-white uppercase tracking-wider" data-id="element-326">
        After
      </div>

      {/* Before Image (Clipped) */}
      <div className="absolute inset-0 w-full h-full overflow-hidden" style={{
      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
    }} data-id="element-327">
        <img src={beforeImage} alt="Before" className="absolute inset-0 w-full h-full object-cover" data-id="element-328" />
        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur px-3 py-1 rounded text-xs font-bold text-white uppercase tracking-wider" data-id="element-329">
          Before
        </div>
      </div>

      {/* Slider Handle */}
      <div className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10" style={{
      left: `${sliderPosition}%`
    }} data-id="element-330">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-331">
          <MoveHorizontal className="w-4 h-4 text-black" data-id="element-332" />
        </div>
      </div>
    </div>;
}
function FeatureTabs() {
  const [activeTab, setActiveTab] = useState(0);
  const features = [{
    title: 'User-Specific Metrics',
    description: "Personalized dashboard widgets that adapt to the user's role and daily tasks.",
    icon: Users,
    image: 'https://cdn.magicpatterns.com/uploads/3Evunh1xY995U75WKRhWBQ/DesignRevision01.jpg'
  }, {
    title: 'Critical Alerts System',
    description: 'Intelligent notification center that prioritizes urgent items requiring immediate attention.',
    icon: Shield,
    image: 'https://cdn.magicpatterns.com/uploads/tgN78wRfQpDEWHFKJikZWP/DesignRevision02-A.jpg'
  }, {
    title: 'Cash-Flow Visualization',
    description: 'Interactive charts providing real-time visibility into liquidity and forecasting.',
    icon: Activity,
    image: 'https://cdn.magicpatterns.com/uploads/s4DrpawEkcprNwnxSxsfdd/Wireframe.jpg'
  }, {
    title: 'Modular Payments',
    description: 'Seamless integration of ACH, Real-Time, and Tokenized payment workflows.',
    icon: Grid,
    image: 'https://cdn.magicpatterns.com/uploads/2P29d6VocNXExZrZjCYicN/FinalDesign.jpg'
  }];
  return <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-333">
      <div className="space-y-4" data-id="element-334">
        {features.map((feature, index) => <button key={index} onClick={() => setActiveTab(index)} className={`w-full text-left p-6 rounded-xl transition-all duration-300 border ${activeTab === index ? 'bg-white/10 border-white/20 shadow-lg' : 'bg-transparent border-transparent hover:bg-white/5'}`} data-id="element-335">
            <div className="flex items-start gap-4" data-id="element-336">
              <div className={`p-2 rounded-lg ${activeTab === index ? 'bg-white text-black' : 'bg-white/5 text-gray-400'}`} data-id="element-337">
                <feature.icon className="w-5 h-5" data-id="element-338" />
              </div>
              <div data-id="element-339">
                <h4 className={`font-bold mb-1 ${activeTab === index ? 'text-white' : 'text-gray-400'}`} data-id="element-340">
                  {feature.title}
                </h4>
                <p className={`text-sm ${activeTab === index ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-341">
                  {feature.description}
                </p>
              </div>
            </div>
          </button>)}
      </div>
      <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-zinc-900" data-id="element-342">
        {features.map((feature, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-500 ${activeTab === index ? 'opacity-100' : 'opacity-0'}`} data-id="element-343">
            <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" data-id="element-344" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-345" />
          </div>)}
      </div>
    </div>;
}
function ChallengeAccordionSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const challenges = [{
    icon: Grid,
    title: 'Scattered Information',
    description: 'Essential data was dispersed across multiple screens, requiring excessive navigation.'
  }, {
    icon: Shield,
    title: 'Entitlement Complexity',
    description: "User entitlements weren't handled gracefully, often showing irrelevant options or hiding critical data."
  }, {
    icon: Activity,
    title: 'Reactive, Not Proactive',
    description: 'Lack of critical alerts meant users were reacting to issues rather than managing them proactively.'
  }];
  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-start" data-id="element-346">
      <div className="md:col-span-1" data-id="element-347">
        <div className="space-y-3" data-id="element-348">
          {challenges.map((challenge, index) => {
          const isOpen = openIndex === index;
          const Icon = challenge.icon;
          return <div key={index} className={`rounded-xl border transition-all duration-300 ease-out overflow-hidden ${isOpen ? 'bg-white/10 border-white/20' : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.05] hover:border-white/10'}`} data-id="element-349">
                <button onClick={() => toggleAccordion(index)} className="w-full p-5 flex items-center justify-between gap-4 text-left group" data-id="element-350">
                  <div className="flex items-center gap-3" data-id="element-351">
                    <div className={`p-2 rounded-lg transition-all duration-300 ${isOpen ? 'bg-white/20' : 'bg-white/5 group-hover:bg-white/10'}`} data-id="element-352">
                      <Icon className={`w-4 h-4 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-353" />
                    </div>
                    <h4 className={`font-medium text-sm transition-colors duration-300 ${isOpen ? 'text-white' : 'text-gray-300 group-hover:text-white'}`} data-id="element-354">
                      {challenge.title}
                    </h4>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-all duration-300 flex-shrink-0 ${isOpen ? 'text-white rotate-180' : 'text-gray-500 group-hover:text-gray-400'}`} data-id="element-355" />
                </button>
                <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-356">
                  <div className="overflow-hidden" data-id="element-357">
                    <p className="px-5 pb-5 text-sm text-gray-400 leading-relaxed" data-id="element-358">
                      {challenge.description}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
      <div className="md:col-span-2" data-id="element-359">
        <div className="relative group" data-id="element-360">
          <div className="absolute -inset-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000" data-id="element-361"></div>
          <div className="relative bg-zinc-900 rounded-xl overflow-hidden border border-white/10" data-id="element-362">
            <img src="https://cdn.magicpatterns.com/uploads/anFSYZnsbr5GGPExBX282D/BeforeImage.jpg" alt="Legacy Interface" className="w-full h-auto opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-363" />
          </div>
        </div>
        <p className="mt-4 text-xs font-medium text-white/70 uppercase tracking-wider text-right" data-id="element-364">
          Legacy Interface
        </p>
      </div>
    </div>;
}
function EntitlementWorkshop() {
  const [activeView, setActiveView] = useState<'matrix' | 'flow'>('matrix');
  const [hoveredCell, setHoveredCell] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const widgets = ['Open Tasks', 'Quick Actions', 'Account Balances', 'Reports', 'Insights', 'File Imports', 'Recent Updates'];
  const services = [{
    name: 'ACH Origination',
    affects: [0, 1, 4, 6]
  }, {
    name: 'Real-time Payments',
    affects: [0, 6]
  }, {
    name: 'Balance Reporting',
    affects: [2, 3]
  }, {
    name: 'Wire Payments',
    affects: [0, 1, 6]
  }, {
    name: 'Tokenized Payments',
    affects: [0, 1, 6]
  }, {
    name: 'Account Recon',
    affects: [0, 2, 4, 6]
  }, {
    name: 'Enhanced Liquidity',
    affects: [2, 4, 6]
  }, {
    name: 'Book Transfer',
    affects: [0, 1, 4, 6]
  }, {
    name: 'Tax Payments',
    affects: [0, 6]
  }, {
    name: 'Analytics',
    affects: [2, 3, 4]
  }];
  const personas = [{
    name: 'Report-Only User',
    role: 'Financial Analyst',
    entitlements: ['Balance Reporting', 'Analytics', 'Check History'],
    enabledWidgets: [2, 3, 4]
  }, {
    name: 'ACH Specialist',
    role: 'Payment Operations',
    entitlements: ['ACH Origination', 'ACH File Verification', 'Balance Reporting'],
    enabledWidgets: [0, 1, 2, 3, 4, 5]
  }, {
    name: 'Treasury Admin',
    role: 'Full Access',
    entitlements: ['All Services', 'User Management', 'System Config'],
    enabledWidgets: [0, 1, 2, 3, 4, 5, 6]
  }];
  return <div className="bg-zinc-900 rounded-2xl overflow-hidden border border-white/5 backdrop-blur-xl" data-id="element-365">
      {/* Header */}
      <div className="p-8 md:p-10 border-b border-white/5" data-id="element-366">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6" data-id="element-367">
          <div className="flex-1" data-id="element-368">
            <h3 className="text-2xl font-bold text-white mb-3" data-id="element-369">
              Workshop Spotlight: Entitlement Modeling
            </h3>
            <p className="text-gray-500 mb-6 max-w-2xl" data-id="element-370">
              One of the biggest challenges was handling the complex matrix of
              user permissions. We conducted a dedicated workshop to map out
              every possible user state.
            </p>
            <ul className="space-y-2" data-id="element-371">
              <li className="flex items-center gap-2 text-sm text-gray-400" data-id="element-372">
                <span className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-373" />
                Defined 5 core user personas
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-400" data-id="element-374">
                <span className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-375" />
                Mapped 50+ permission combinations
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-400" data-id="element-376">
                <span className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-377" />
                Created "empty states" that guide rather than block
              </li>
            </ul>
          </div>
          {/* Tab Toggle */}
          <div className="flex bg-white/[0.03] rounded-full p-1 border border-white/5 backdrop-blur-sm" data-id="element-378">
            <button onClick={() => setActiveView('matrix')} className={`px-5 py-2 text-sm font-medium rounded-full transition-all ${activeView === 'matrix' ? 'bg-gray-500 text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-379">
              Matrix View
            </button>
            <button onClick={() => setActiveView('flow')} className={`px-5 py-2 text-sm font-medium rounded-full transition-all ${activeView === 'flow' ? 'bg-gray-500 text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-380">
              Flow View
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 md:p-10 bg-gradient-to-b from-zinc-900 to-zinc-800" data-id="element-381">
        {activeView === 'matrix' ? <div data-id="element-382">
            {/* Legend */}
            <div className="flex items-center justify-between mb-6" data-id="element-383">
              <p className="text-sm text-gray-600" data-id="element-384">
                Hover over cells to see relationships
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-385">
                <span className="w-3 h-3 bg-white rounded-full" data-id="element-386" />
                Service affects widget
              </div>
            </div>

            {/* Matrix Grid */}
            <div className="overflow-x-auto" data-id="element-387">
              <table className="w-full" data-id="element-388">
                <thead data-id="element-389">
                  <tr data-id="element-390">
                    <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-40" data-id="element-391" />
                    {widgets.map((widget, colIndex) => <th key={widget} className={`p-3 text-center text-xs font-medium uppercase tracking-wider transition-all min-w-[80px] ${hoveredCell?.col === colIndex ? 'text-purple-300 bg-gradient-to-b from-blue-600/20 via-purple-600/20 to-pink-600/20' : 'text-gray-600'}`} data-id="element-392">
                        {widget.split(' ').map((word, i) => <span key={i} className="block" data-id="element-393">
                            {word}
                          </span>)}
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-394">
                  {services.map((service, rowIndex) => <tr key={service.name} data-id="element-395">
                      <td className={`p-3 text-sm font-medium transition-all ${hoveredCell?.row === rowIndex ? 'text-purple-300 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20' : 'text-gray-400'}`} data-id="element-396">
                        {service.name}
                      </td>
                      {widgets.map((_, colIndex) => {
                  const isActive = service.affects.includes(colIndex);
                  const isRowHighlighted = hoveredCell?.row === rowIndex;
                  const isColHighlighted = hoveredCell?.col === colIndex;
                  const isHighlighted = isRowHighlighted || isColHighlighted;
                  return <td key={colIndex} className="p-3" onMouseEnter={() => setHoveredCell({
                    row: rowIndex,
                    col: colIndex
                  })} onMouseLeave={() => setHoveredCell(null)} data-id="element-397">
                            <div className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center transition-all cursor-default backdrop-blur-sm border ${isHighlighted ? isActive ? 'bg-gradient-to-br from-blue-600/30 via-purple-600/30 to-pink-600/30 border-purple-500/30' : 'bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10 border-white/5' : 'bg-white/[0.02] border-white/5'}`} data-id="element-398">
                              {isActive && <span className="w-3 h-3 rounded-full bg-white transition-all" data-id="element-399" />}
                            </div>
                          </td>;
                })}
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Footer Note */}
            <div className="mt-8 flex items-start gap-2 text-xs text-gray-400" data-id="element-400">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-401" />
              <p data-id="element-402">
                This simplified view shows 10 key services. The full matrix maps
                50+ permission combinations across all dashboard widgets,
                ensuring users only see relevant functionality.
              </p>
            </div>
          </div> : <div data-id="element-403">
            <div className="mb-6" data-id="element-404">
              <h4 className="text-lg font-semibold text-white mb-1" data-id="element-405">
                Permission Flow by Persona
              </h4>
              <p className="text-sm text-gray-500" data-id="element-406">
                How entitlements shape the user experience
              </p>
            </div>

            {/* Persona Cards */}
            <div className="space-y-4" data-id="element-407">
              {personas.map(persona => <div key={persona.name} className="border border-white/5 rounded-xl p-6 hover:border-white/10 transition-all bg-white/[0.02] backdrop-blur-sm hover:bg-white/[0.04]" data-id="element-408">
                  <div className="grid md:grid-cols-[200px_1fr_1fr] gap-6 items-center" data-id="element-409">
                    {/* Persona */}
                    <div className="flex items-center gap-3" data-id="element-410">
                      <div className="w-12 h-12 bg-white/[0.03] rounded-xl flex items-center justify-center border border-white/5 backdrop-blur-sm" data-id="element-411">
                        <User className="w-6 h-6 text-gray-500" data-id="element-412" />
                      </div>
                      <div data-id="element-413">
                        <h5 className="font-semibold text-white" data-id="element-414">
                          {persona.name}
                        </h5>
                        <p className="text-sm text-gray-500" data-id="element-415">{persona.role}</p>
                      </div>
                    </div>

                    {/* Arrow + Entitlements */}
                    <div className="flex items-center gap-4" data-id="element-416">
                      <ArrowRight className="w-5 h-5 text-gray-700 hidden md:block" data-id="element-417" />
                      <div data-id="element-418">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-419">
                          Entitlements
                        </p>
                        <ul className="space-y-1" data-id="element-420">
                          {persona.entitlements.map(ent => <li key={ent} className="flex items-center gap-2 text-sm text-gray-400" data-id="element-421">
                              <span className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-422" />
                              {ent}
                            </li>)}
                        </ul>
                      </div>
                    </div>

                    {/* Arrow + Dashboard Widgets */}
                    <div className="flex items-center gap-4" data-id="element-423">
                      <ArrowRight className="w-5 h-5 text-gray-700 hidden md:block" data-id="element-424" />
                      <div data-id="element-425">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-426">
                          Dashboard Widgets
                        </p>
                        <div className="grid grid-cols-2 gap-2" data-id="element-427">
                          {widgets.map((widget, widgetIndex) => {
                      const isEnabled = persona.enabledWidgets.includes(widgetIndex);
                      return <div key={widget} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all backdrop-blur-sm ${isEnabled ? 'bg-white/10 text-white border border-white/10' : 'bg-white/[0.02] text-gray-600 border border-white/5'}`} data-id="element-428">
                                {isEnabled ? <Check className="w-3 h-3" data-id="element-429" /> : <X className="w-3 h-3" data-id="element-430" />}
                                {widget}
                              </div>;
                    })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Footer */}
            <div className="mt-8 rounded-xl p-6 border border-white/5 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 backdrop-blur-sm" data-id="element-431">
              <h5 className="font-semibold text-white mb-2" data-id="element-432">
                Adaptive Interface Design
              </h5>
              <p className="text-sm text-gray-400" data-id="element-433">
                The dashboard intelligently adapts to each user's entitlements,
                hiding irrelevant widgets and surfacing only the tools they
                need. This reduces cognitive load and creates a personalized
                experience for every role.
              </p>
            </div>
          </div>}
      </div>
    </div>;
}
export function DashboardCaseStudy() {
  const navLinks = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Work',
    href: '/#work'
  }, {
    label: 'About',
    href: '/aboutme'
  }];
  return <div className="min-h-screen bg-black text-white selection:bg-white selection:text-black font-sans" data-id="element-434">
      <Navbar brand="Taylor Sams" links={navLinks} contactEmail="taylorjsams@gmail.com" data-id="element-435" />

      <main data-id="element-436">
        <CaseStudyHero title="From Chaos to Clarity" description="Crafting a Streamlined Dashboard for Intelligent Treasury Operations" role="Product Designer" duration="5 Weeks" tools={['Figma']} industry="Financial Services" tags={['UX Research', 'Strategy', 'Interaction Design']} data-id="element-437" />

        {/* Content with Sidebar Navigation */}
        <div className="flex gap-12 max-w-7xl mx-auto px-6" data-id="element-438">
          <ContentNavigationChaos data-id="element-439" />

          <div className="flex-1 min-w-0" data-id="element-440">
            {/* 1. About Project (Overview) */}
            <CaseStudySection id="overview" darker data-id="element-441">
              <h3 className="text-2xl font-bold text-white mb-6" data-id="element-442">
                About the Project
              </h3>
              <div className="space-y-6 text-gray-400 text-lg leading-relaxed" data-id="element-443">
                <p data-id="element-444">
                  Before this project, treasury teams were navigating a
                  challenging landscape where the data essential for strategic
                  decisions was fragmented, hidden in silos, or sometimes, just
                  wasn't there at all. This led to serious inefficiencies and a
                  constant struggle to understand 'what needs my attention right
                  now?'
                </p>
                <p data-id="element-445">
                  Add to that the complexity of serving a wide spectrum of
                  clients with vastly different product sets, and the challenge
                  became immense. Our goal was ambitious: to eliminate these
                  blind spots and create a dashboard that wasn't just a data
                  aggregator, but an intelligent daily guide.
                </p>
                <p data-id="element-446">
                  We designed a flexible dashboard that is highly adaptable,
                  pulling together all relevant information for each unique
                  client profile, highlighting critical tasks and alerts, and
                  empowering users to proactively manage their day with clarity
                  and confidence. It's about turning data frustration into
                  decisive action, every single morning.
                </p>
              </div>
            </CaseStudySection>

            {/* 2. The Challenge */}
            <CaseStudySection id="challenge" title="The Challenge" sectionNumber="Fragmented Data & Limited Visibility" data-id="element-447">
              <p className="text-xl text-gray-300 mb-12 font-light max-w-4xl" data-id="element-448">
                The legacy dashboard failed to cover essential payment functions
                like ACH, Real-Time, and Tokenized payments. Users struggled
                with clarity on outstanding items and lacked meaningful
                cash-flow insights, leading to operational inefficiencies.
              </p>
              <ChallengeAccordionSection data-id="element-449" />
            </CaseStudySection>

            {/* 3. The Solution */}
            <CaseStudySection id="solution" title="The Solution" sectionNumber="Intelligent, Modular, Adaptive" darker data-id="element-450">
              <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-451">
                <p className="text-xl text-gray-300 font-light" data-id="element-452">
                  A complete reimagining of the treasury experience, focusing on
                  clarity, actionable insights, and a modular architecture that
                  adapts to user needs.
                </p>
              </div>
              <div className="mb-16 rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-blue-900/20" data-id="element-453">
                <img src="https://cdn.magicpatterns.com/uploads/2P29d6VocNXExZrZjCYicN/FinalDesign.jpg" alt="Final Dashboard Design" className="w-full h-auto" data-id="element-454" />
              </div>
              <div className="grid md:grid-cols-3 gap-8" data-id="element-455">
                <div className="text-center p-6" data-id="element-456">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-blue-500/10 flex items-center justify-center rotate-3 hover:rotate-0 transition-transform" data-id="element-457">
                    <Layout className="w-8 h-8 text-blue-400" data-id="element-458" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-3" data-id="element-459">
                    Modular Architecture
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-460">
                    A flexible grid system that allows for the modular
                    activation of payment services based on client needs.
                  </p>
                </div>
                <div className="text-center p-6" data-id="element-461">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-purple-500/10 flex items-center justify-center -rotate-3 hover:rotate-0 transition-transform" data-id="element-462">
                    <Zap className="w-8 h-8 text-purple-400" data-id="element-463" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-3" data-id="element-464">
                    Actionable Intelligence
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-465">
                    Critical alerts and pending tasks are surfaced immediately,
                    empowering proactive decision-making.
                  </p>
                </div>
                <div className="text-center p-6" data-id="element-466">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-green-500/10 flex items-center justify-center rotate-3 hover:rotate-0 transition-transform" data-id="element-467">
                    <Users className="w-8 h-8 text-green-400" data-id="element-468" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-3" data-id="element-469">
                    Entitlement-Aware
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-470">
                    Smart filtering ensures users only see what they have access
                    to, reducing cognitive load and interface clutter.
                  </p>
                </div>
              </div>
            </CaseStudySection>

            {/* 4. Methodology */}
            <CaseStudySection id="methodology" title="Methodology" sectionNumber="the design process" data-id="element-471">
              <div className="mb-20" data-id="element-472">
                <div className="relative" data-id="element-473">
                  {/* Timeline Line */}
                  <div className="absolute top-2 left-0 right-0 h-px bg-white/20 hidden md:block" data-id="element-474" />
                  <div className="grid md:grid-cols-4 gap-8" data-id="element-475">
                    {[{
                    phase: 'Discovery',
                    desc: 'Stakeholder interviews and user panel insights to identify key pain points.'
                  }, {
                    phase: 'Workshops',
                    desc: 'Collaborative sessions for journey mapping and entitlement modeling.'
                  }, {
                    phase: 'Prototyping',
                    desc: 'Iterative wireframing and feedback loops.'
                  }, {
                    phase: 'Validation',
                    desc: 'Usability testing with treasury heads to validate the new architecture.'
                  }].map((step, i) => <div key={i} className="relative" data-id="element-476">
                        <div className="flex justify-center md:justify-center mb-6" data-id="element-477">
                          <div className="w-4 h-4 rounded-full bg-black border-2 border-white/60 relative z-10" data-id="element-478" />
                        </div>
                        <div className="text-center md:text-center" data-id="element-479">
                          <h4 className="font-bold text-white mb-2 text-sm uppercase tracking-wide" data-id="element-480">
                            {step.phase}
                          </h4>
                          <p className="text-xs text-gray-400 leading-relaxed" data-id="element-481">
                            {step.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
              <EntitlementWorkshop data-id="element-482" />
            </CaseStudySection>

            {/* 5. Key Features */}
            <CaseStudySection id="features" title="Key Features" sectionNumber="Designed For Performance" darker data-id="element-483">
              <FeatureTabs data-id="element-484" />
            </CaseStudySection>

            {/* 6. Impact and Results */}
            <CaseStudySection id="impact" title="Impact & Results" sectionNumber="Measurable Success" data-id="element-485">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20" data-id="element-486">
                {[{
                label: 'Faster Task Completion',
                value: '40%',
                icon: Clock
              }, {
                label: 'User Satisfaction',
                value: '85%',
                icon: Star
              }, {
                label: 'Fewer Support Tickets',
                value: '60%',
                icon: MessageSquare
              }, {
                label: 'Faster Decisions',
                value: '3x',
                icon: Zap
              }].map((metric, i) => <div key={i} className="bg-white/5 p-6 rounded-2xl border border-white/10 text-center hover:bg-white/10 transition-colors" data-id="element-487">
                    <metric.icon className="w-6 h-6 text-gray-400 mx-auto mb-4" data-id="element-488" />
                    <div className="text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-489">
                      {metric.value}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-490">
                      {metric.label}
                    </div>
                  </div>)}
              </div>
              <div className="space-y-8" data-id="element-491">
                <div className="text-center" data-id="element-492">
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-493">
                    Visual Evolution
                  </h3>
                  <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-494">
                    Drag the slider to compare the legacy interface with our
                    redesigned solution.
                  </p>
                </div>
                <BeforeAfterSlider beforeImage="https://cdn.magicpatterns.com/uploads/anFSYZnsbr5GGPExBX282D/BeforeImage.jpg" afterImage="https://cdn.magicpatterns.com/uploads/2P29d6VocNXExZrZjCYicN/FinalDesign.jpg" data-id="element-495" />
              </div>
            </CaseStudySection>
          </div>
        </div>

        {/* Bottom Navigation */}
        <nav className="border-t border-white/10 bg-zinc-950 py-16 px-6" data-id="element-496">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-8" data-id="element-497">
            <Link to="/" className="group flex items-center gap-4 text-left" data-id="element-498">
              <div className="p-4 rounded-full border border-white/10 bg-white/5 group-hover:bg-white group-hover:text-black transition-all" data-id="element-499">
                <ArrowLeft className="w-6 h-6" data-id="element-500" />
              </div>
              <div data-id="element-501">
                <span className="block text-sm text-gray-500 uppercase tracking-wider mb-1" data-id="element-502">
                  Back to
                </span>
                <span className="text-xl font-bold text-white group-hover:text-gray-300 transition-colors" data-id="element-503">
                  Home
                </span>
              </div>
            </Link>
            <a href="https://taylorsams.design/verizon" className="group flex items-center gap-4 text-right flex-row-reverse md:flex-row" data-id="element-504">
              <div className="text-right" data-id="element-505">
                <span className="block text-sm text-gray-500 uppercase tracking-wider mb-1" data-id="element-506">
                  Next Project
                </span>
                <span className="text-xl font-bold text-white group-hover:text-gray-300 transition-colors" data-id="element-507">
                  Verizon BUSS 2.0
                </span>
              </div>
              <div className="p-4 rounded-full border border-white/10 bg-white/5 group-hover:bg-white group-hover:text-black transition-all" data-id="element-508">
                <ArrowRight className="w-6 h-6" data-id="element-509" />
              </div>
            </a>
          </div>
        </nav>

        {/* Simple Footer */}
        <footer className="py-12 text-center text-gray-600 text-sm border-t border-white/10" data-id="element-510">
          <p data-id="element-511">© {new Date().getFullYear()} Taylor Sams. All rights reserved.</p>
        </footer>
      </main>
    </div>;
}